import { Fragment, type ReactNode, useRef, useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/MagnifyingGlass";
import { XIcon } from "@phosphor-icons/react/X";
import { Icon } from "../Icons/Icon";
import { PortalContextReset } from "./Internal/PortalContextReset";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";
import { useIsFormItemError } from "../FormLayout/FormItemErrorProvider";
import { useFormItemWarningMark } from "./Internal/FormItemWarningMark";
import { useControlBarSlot } from "../PageLayout/Internal/ControlBarContext";
import { useControlPresentation } from "./Internal/useControlPresentation";
import {
  DROPDOWN_MENU,
  DROPDOWN_SEARCH_BOX,
  triggerUnderline,
  dropdownRow,
  dropdownDescription,
} from "./Internal/dropdownStyles";
import type { BadgeVariant } from "../Display/Badge";
import {
  inputWidthClasses as widthClasses,
  type InputWidth,
} from "./inputWidth";

interface DropdownOption {
  label: string;
  value: string;
  description?: string | undefined;
  /**
   * Extra text `searchable` matches against but never shows — e.g. the full
   * address behind a business name. Without it, making an option findable by
   * something it doesn't display means overloading `description`, and then
   * `renderOption` has to take over the row just to hide it again.
   */
  searchText?: string | undefined;
  icon?: ReactNode;
  /** Optional section header to group this option under. Options sharing a
   *  group render together beneath an uppercase header (in first-appearance
   *  order). Omit on every option for a flat, unheadered list. */
  group?: string | undefined;
  /** When true, the option is shown but greyed out and cannot be selected.
   *  Use for choices that are visible-but-unavailable (e.g. an item that is
   *  already configured) so the user understands it exists without being able
   *  to pick it. Prefer this over hiding the option. */
  disabled?: boolean | undefined;
  /** Short reason shown as a native tooltip when hovering a disabled option,
   *  explaining why it can't be picked (e.g. a carrier service restriction). */
  disabledReason?: string | undefined;
}

interface DropdownGroup {
  /** Matches the `group` on an option. */
  name: string;
  /** Rendered after the header text — e.g. the marker icon that tells the
   *  reader what puts a location in this group. */
  icon?: ReactNode;
  /**
   * Shown in place of the options when the group has none, to explain the gap
   * rather than leave the reader wondering. Without it an empty group is
   * dropped entirely, which is the sensible default.
   */
  emptyMessage?: ReactNode;
}

interface DropdownProps {
  options: DropdownOption[];
  /**
   * Declares the groups up front: their order, header decoration, and what to
   * say when one is empty. Omit it and groups are inferred from the options in
   * first-appearance order, with empty ones simply absent — which is all a flat
   * or naturally-populated list needs.
   */
  groups?: DropdownGroup[] | undefined;
  value?: string | undefined;
  onChange?: ((value: string) => void) | undefined;
  onClear?: (() => void) | undefined;
  placeholder?: string | undefined;
  /** Constrain the trigger width (defaults to full). Matches TextInput's width tokens. */
  width?: InputWidth | undefined;
  searchable?: boolean | undefined;
  searchPlaceholder?: string | undefined;
  renderOption?: ((option: DropdownOption) => ReactNode) | undefined;
  /** Cap the number of rendered options to avoid DOM bloat with large lists. Requires search to narrow results. */
  maxDisplayed?: number | undefined;
  /** Optional per-item color hint. When provided, renders a small filled
   *  dot using the matching badge variant before each option label and
   *  before the selected label in the trigger. Use it to category-tag
   *  options at a glance (e.g. document types, status filters). */
  colorForItem?: ((value: string) => BadgeVariant | undefined) | undefined;
  /** Greys out the trigger and prevents opening. The selected value stays visible. */
  disabled?: boolean | undefined;
  "data-testid"?: string | undefined;
}

export type { DropdownOption, DropdownGroup };

const colorDotClasses: Record<Exclude<BadgeVariant, "auto">, string> = {
  info: "bg-blue",
  success: "bg-green",
  warning: "bg-orange",
  amber: "bg-amber-400",
  danger: "bg-red",
  purple: "bg-purple",
  neutral: "bg-foreground/30",
  dark: "bg-foreground/60",
};

function ColorDot_({ variant }: { variant: BadgeVariant | undefined }) {
  if (!variant || variant === "auto") return undefined;
  return (
    <span
      className={`inline-block size-2 shrink-0 rounded-full ${colorDotClasses[variant]}`}
    />
  );
}

// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function Dropdown({
  options,
  groups,
  value,
  onChange,
  onClear,
  placeholder,
  width = "full",
  searchable,
  searchPlaceholder = "Search...",
  renderOption,
  maxDisplayed,
  colorForItem,
  disabled = false,
  "data-testid": dataTestid,
}: DropdownProps) {
  const markDirty = useMarkDialogDirty();
  const [open, setOpen] = useState(false);
  // The menu is at least as wide as its trigger, but that width is measured
  // once on open and pinned in whole pixels. Reading it live from
  // --radix-popover-trigger-width instead makes the menu's own size depend on
  // a value floating-ui recomputes every time it repositions; with a
  // fractional trigger width (a two-column grid inside a dialog gives one) the
  // two chase each other a pixel at a time and never settle.
  // oxlint-disable-next-line fs/no-null -- DOM refs require null
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(
    undefined,
  );
  const [search, setSearch] = useState("");

  const resolvedPlaceholder =
    placeholder ?? options.find((opt) => opt.value === "")?.label;
  const validOptions = options.filter((opt) => opt.value !== "");
  const isSearchable = searchable ?? validOptions.length > 8;

  const matched =
    isSearchable && search
      ? validOptions.filter((opt) => {
          const q = search.toLowerCase();
          return (
            opt.label.toLowerCase().includes(q) ||
            opt.value.toLowerCase().includes(q) ||
            (opt.description?.toLowerCase().includes(q) ?? false) ||
            (opt.searchText?.toLowerCase().includes(q) ?? false)
          );
        })
      : validOptions;

  // Cap rendered items to avoid rendering thousands of DOM nodes at once.
  const isTruncated =
    maxDisplayed !== undefined && matched.length > maxDisplayed;
  const filtered = isTruncated ? matched.slice(0, maxDisplayed) : matched;

  const selected = validOptions.find((opt) => opt.value === value);

  function select(optValue: string) {
    markDirty?.();
    onChange?.(optValue);
    setOpen(false);
    setSearch("");
  }

  // Bucket options under section headers when any option declares a `group`,
  // preserving first-appearance order. With no groups this is a single
  // headerless section — i.e. the flat list, unchanged.
  const sections: {
    group: string | undefined;
    items: DropdownOption[];
    icon?: ReactNode;
    emptyMessage?: ReactNode;
  }[] = [];
  // Declared groups come first, in the order given, so a group with an
  // emptyMessage still has a place to appear even with nothing in it.
  for (const g of groups ?? []) {
    sections.push({
      group: g.name,
      items: [],
      ...(g.icon !== undefined && { icon: g.icon }),
      ...(g.emptyMessage !== undefined && { emptyMessage: g.emptyMessage }),
    });
  }
  for (const opt of filtered) {
    const existing = sections.find((s) => s.group === opt.group);
    if (existing) existing.items.push(opt);
    else sections.push({ group: opt.group, items: [opt] });
  }
  // A declared group with no options and nothing to say about it is noise.
  // While searching, drop every empty group — an empty-state message explains
  // "you have no favourites", not "your search didn't match".
  const visibleSections = sections.filter(
    (s) =>
      s.items.length > 0 || (s.emptyMessage !== undefined && search === ""),
  );
  const hasGroups = visibleSections.some((s) => s.group !== undefined);

  const isError = useIsFormItemError();
  // Holds the warning explanation open while the trigger has focus — see
  // `useFormItemWarningMark`. `open` counts as focused: the popover takes focus
  // away from the trigger, and the explanation shouldn't vanish mid-choice.
  const [triggerFocused, setTriggerFocused] = useState(false);
  const warningMark = useFormItemWarningMark({
    focused: triggerFocused || open,
  });
  // Controls default to a dense boxed trigger; the spacious underline is opt-in for
  // low-density customer flows (quote/booking, login).
  const presentation = useControlPresentation();
  const boxed = presentation === "boxed";
  // Inside a control/filter bar, the underline trigger shrinks to text-sm so
  // it lines up with sibling controls rather than using the large Cargo form size.
  const inControlBar = useControlBarSlot() !== false;
  const compact = !boxed && inControlBar;
  // Inside a filter/control bar the boxed trigger tightens (less padding).
  // Outside one it stays h-8 so it lines up with a boxed TextInput beside it.
  const boxedCompact = boxed && inControlBar;

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        if (disabled) return;
        if (o) {
          const width = triggerRef.current?.getBoundingClientRect().width;
          setTriggerWidth(width === undefined ? undefined : Math.floor(width));
        }
        setOpen(o);
        if (!o) setSearch("");
      }}
      modal={false}
    >
      <PopoverPrimitive.Trigger
        ref={triggerRef}
        disabled={disabled}
        onFocus={() => setTriggerFocused(true)}
        onBlur={() => setTriggerFocused(false)}
        className={
          boxed
            ? `group flex ${boxedCompact ? "h-[26px]" : "h-8"} ${widthClasses[width]} cursor-pointer items-center justify-between gap-2 rounded-md border bg-card ${boxedCompact ? "px-1.5 text-xs" : "px-3 text-sm"} font-medium text-foreground transition-colors focus:outline-none ${disabled ? "pointer-events-none opacity-40" : ""} ${isError ? "border-red" : "border-border-strong focus:border-brand"}`
            : `group flex ${widthClasses[width]} cursor-pointer items-center justify-between gap-2 bg-transparent px-0 text-left transition-colors focus:outline-none ${compact ? "pt-1 text-sm" : "pt-2 text-xl leading-7"} ${disabled ? "pointer-events-none opacity-40" : ""} ${triggerUnderline(isError)}`
        }
        {...(dataTestid !== undefined && { "data-testid": dataTestid })}
      >
        {selected ? (
          <span
            className={`flex items-center gap-2 truncate ${boxed ? "font-medium text-foreground" : "font-bold text-foreground"}`}
          >
            {colorForItem && (
              <ColorDot_ variant={colorForItem(selected.value)} />
            )}
            {selected.icon}
            <span>{selected.label}</span>
          </span>
        ) : (
          <span className="font-normal text-faint">
            {resolvedPlaceholder ?? "Select..."}
          </span>
        )}
        <span className="flex shrink-0 items-center gap-1">
          {warningMark}
          {onClear && selected && (
            <span
              role="button"
              aria-label="Clear selection"
              onPointerDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                markDirty?.();
                onClear();
              }}
              className="cursor-pointer text-muted-foreground transition-opacity hover:text-foreground"
            >
              <Icon icon={XIcon} size="sm" weight="bold" />
            </span>
          )}
          <span className="flex items-center transition-transform group-data-[state=open]:rotate-180">
            <Icon
              icon={CaretDownIcon}
              size="sm"
              color="text-muted-foreground"
            />
          </span>
        </span>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          {...(triggerWidth !== undefined && {
            style: { minWidth: `${triggerWidth}px` },
          })}
          className={DROPDOWN_MENU}
          onOpenAutoFocus={(e) => {
            if (isSearchable) e.preventDefault();
          }}
        >
          {/* Portaled content (incl. caller-supplied renderOption) must not
              inherit the trigger surface's layout contexts — see
              PortalContextReset. */}
          <PortalContextReset>
            {isSearchable && (
              <div className="px-2 pt-2 pb-1">
                <div className={DROPDOWN_SEARCH_BOX}>
                  <Icon
                    icon={MagnifyingGlassIcon}
                    size="sm"
                    color="text-muted-foreground"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-content-faint"
                    autoFocus
                  />
                </div>
              </div>
            )}
            <div
              className="py-1"
              style={{
                maxHeight: 300,
                overflowY: "auto",
                overscrollBehavior: "contain",
              }}
            >
              {visibleSections.map((section) => (
                <Fragment key={section.group ?? "__ungrouped__"}>
                  {hasGroups && section.group !== undefined && (
                    <div className="flex items-center gap-1 px-3 pt-2 pb-1 text-[11px] font-semibold tracking-wide text-content-muted uppercase">
                      {section.group}
                      {section.icon}
                    </div>
                  )}
                  {section.items.length === 0 &&
                    section.emptyMessage !== undefined && (
                      <div className="px-3 pb-1.5 text-xs text-content-muted">
                        {section.emptyMessage}
                      </div>
                    )}
                  {section.items.map((opt) => {
                    const isSelected = value === opt.value;
                    const isDisabled = opt.disabled ?? false;
                    return (
                      <div key={opt.value} className="px-1.5 py-0.5">
                        <button
                          type="button"
                          disabled={isDisabled}
                          title={isDisabled ? opt.disabledReason : undefined}
                          {...(dataTestid !== undefined && {
                            "data-testid": `${dataTestid}-option-${opt.value}`,
                          })}
                          onClick={() => select(opt.value)}
                          className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors ${dropdownRow(isSelected, isDisabled)}`}
                        >
                          {renderOption ? (
                            <div className="flex-1">{renderOption(opt)}</div>
                          ) : (
                            <div className="flex-1">
                              <span
                                className={`flex items-center gap-2 ${opt.description ? "text-sm" : "text-[13px]"}`}
                              >
                                {colorForItem && (
                                  <ColorDot_
                                    variant={colorForItem(opt.value)}
                                  />
                                )}
                                {opt.icon}
                                {opt.label}
                              </span>
                              {opt.description && (
                                <div
                                  className={`mt-0.5 text-xs font-normal ${dropdownDescription(
                                    isSelected,
                                    isDisabled,
                                  )}`}
                                >
                                  {opt.description}
                                </div>
                              )}
                            </div>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </Fragment>
              ))}
              {isTruncated && (
                <div className="px-3 py-2 text-center text-xs font-medium text-content-muted">
                  Type to search {matched.length} options
                </div>
              )}
              {filtered.length === 0 && (
                <div className="px-3 py-4 text-center text-xs font-medium text-content-muted">
                  No results
                </div>
              )}
            </div>
          </PortalContextReset>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
